
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  PencilIcon,
  TrashIcon,
  ClipboardDocumentCheckIcon,
  PlusIcon,
  ArrowPathIcon,
  PaperAirplaneIcon,
  XCircleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { deletePartner } from "../../features/admin/PartnerSlice";
import TableComponent from "../../components/TableComponent";
import PartnerModal from "../../components/admin/PartnerModal";
import { toast } from "react-toastify";
import ConfirmDeleteModal from "../../components/ConfirmDeleteModal";
import PartnerApproveModal from "../../components/admin/PartnerApproveModal";
import ApiCustom from "../../utils/ApiCustom";
import PartnerCriteriaModal from "../../components/admin/PartnerCriteriaModal";
import DatePicker from "../../components/DatePicker";
import { formatDate } from "../../utils/helps";
import { renderTextOrUnknown } from "../../utils/helpers";

// --- Helper ---
const getUserPermissions = () => {
  const permissions = localStorage.getItem("permissions");
  return permissions
    ? JSON.parse(permissions)?.map((p) => p.permissionSlug)
    : [];
};

const hasPermission = (requiredPermission) => {
  const userId = localStorage.getItem("userId");
  if (userId == 1) return true;
  const userPermissions = getUserPermissions();
  return userPermissions.includes(requiredPermission);
};

const getTransferImageUrl = (url) => {
  if (!url) return "";

  if (/^https?:\/\//.test(url)) return url;

  return `${process.env.REACT_APP_API_URL || ""}media/${url}`;
};

const getPartnerTransferImageRawUrl = (item) => {
  if (item?.transferImageUrl) return item.transferImageUrl;

  if (item?.CompaniesMustPayMembershipFee?.transferImageUrl) {
    return item.CompaniesMustPayMembershipFee.transferImageUrl;
  }

  if (Array.isArray(item?.membershipFees)) {
    const membershipFee = item.membershipFees.find(
      (fee) => fee?.CompaniesMustPayMembershipFee?.transferImageUrl,
    );

    return membershipFee?.CompaniesMustPayMembershipFee?.transferImageUrl || "";
  }

  return "";
};

const Partners = () => {
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [approvingId, setApprovingId] = useState(null);
  const [isOpenApprove, setIsOpenApprove] = useState(false);
  const [isOpenCriteria, setIsOpenCriteria] = useState(false);
  const [partners, setPartners] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [representList, setRepresentList] = useState([]);
  const [importing, setImporting] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Bộ lọc ngày
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Modal phản hồi
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackNote, setFeedbackNote] = useState("");

  // Modal ảnh chuyển khoản
  const [transferImageOpen, setTransferImageOpen] = useState(false);
  const [transferImageUrl, setTransferImageUrl] = useState("");
  const [transferImageTitle, setTransferImageTitle] = useState("");

  // ------------------ API ------------------

  const handleSendForApproval = async (id) => {
    try {
      await ApiCustom.put(`/users/${id}/send-approve`, {
        approvalStatus: "Gửi duyệt",
      });
      toast.success("Đã gửi duyệt hội viên!");
      initData();
    } catch {
      toast.error("Gửi duyệt thất bại!");
    }
  };

  const handleCancelSendForApproval = async (id) => {
    try {
      await ApiCustom.put(`/users/${id}/send-approve`, {
        approvalStatus: "Đang chờ",
      });
      toast.success("Đã bỏ gửi duyệt!");
      initData();
    } catch {
      toast.error("Bỏ gửi duyệt thất bại!");
    }
  };

  const handleImportExcel = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setImporting(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await ApiCustom.post("/company-import-excel", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(res.data?.message || "Import thành công!");
      initData();
    } catch {
      toast.error("Import thất bại!");
    } finally {
      setImporting(false);
      event.target.value = "";
    }
  };

  const handleOpenDeleteModal = (id) => {
    setDeleteId(id);
    setIsDeleteOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteId(null);
    setIsDeleteOpen(false);
    getPartners();
  };

  const handleOpenModal = (user = null) => {
    setCurrentUser(user);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentUser(null);
    getPartners();
  };

  const handleSubmitModal = async () => {
    await getPartners();
  };

  const handleCloseApprove = (check = false) => {
    setIsOpenApprove(false);
    setApprovingId(null);

    if (check) {
      getPartners();
      toast.success("Duyệt thành công!");
    }
  };

  const handleCloseCriteria = (check) => {
    setIsOpenCriteria(false);
    setApprovingId(null);
    if (check) {
      getPartners();
      toast.success("Đánh giá thành công!");
    }
  };

  const handleDelete = async () => {
    try {
      await dispatch(deletePartner(deleteId)).unwrap();
      handleCloseDeleteModal();
      toast.success("Xóa đối tác thành công!");
    } catch {
      toast.error("Đã xảy ra lỗi khi xóa đối tác!");
    }
  };

  const getUsers = () => {
    ApiCustom.get(`/user-shows`)
      .then((res) => setRepresentList(res.data?.data?.data?.users))
      .catch((err) => console.log(err));
  };

  const getPartners = async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);
      const res = await ApiCustom.get(`company-shows?${params.toString()}`);
      setPartners(res.data.data?.data?.users);
    } catch (err) {
      console.log(err);
    }
  };

  const resetFilters = () => {
    setStartDate("");
    setEndDate("");
    setPage(1);
  };

  const openTransferImageModal = (item) => {
    const imageUrl = getTransferImageUrl(getPartnerTransferImageRawUrl(item));

    if (!imageUrl) return;

    setTransferImageUrl(imageUrl);
    setTransferImageTitle(item?.companyName || "Ảnh chuyển khoản");
    setTransferImageOpen(true);
  };

  const closeTransferImageModal = () => {
    setTransferImageOpen(false);
    setTransferImageUrl("");
    setTransferImageTitle("");
  };

  useEffect(() => {
    if (startDate === "" && endDate === "") {
      getPartners();
    }
  }, [startDate, endDate]);

  const initData = () => {
    getUsers();
    getPartners();
  };

  useEffect(() => {
    initData();
  }, []);

  // ------------------ TABLE ------------------
  const headers = [
    {
      key: "id",
      label: "STT",
      render: (_, index) => {
        return <span>{index + 1}</span>;
      },
    },
    {
      key: "userAvatar",
      label: "Logo",
      render: (item) => (
        <div className="w-16 h-16 rounded-lg overflow-hidden">
          <img
            src={
              item?.userAvatar ||
              `https://ui-avatars.com/api/?name=${item?.companyName}&size=200&background=random&color=fff&bold=true`
            }
            alt={item.companyName}
            className="w-full h-full object-contain"
            loading="lazy"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = `https://ui-avatars.com/api/?name=${item?.companyName}&size=200&background=random&color=fff&bold=true`;
            }}
          />
        </div>
      ),
    },
    {
      key: "companyName",
      label: "Tên hội viên",
      render: (item) => {
        const representativeName = item?.users?.find(
          (e) => e?.CompaniesHasUsers?.isUserRepresentOfCompany,
        )?.userFullName;

        const isIndividual = item?.memberType === "individual";

        return (
          <div className="max-w-[500px] min-w-56 text-wrap">
            <p className="font-medium">{item.companyName}</p>

            <div className="">
              <span
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border ${
                  isIndividual
                    ? "bg-yellow-50 text-yellow-700 border-yellow-200"
                    : "bg-blue-50 text-blue-700 border-blue-200"
                }`}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    isIndividual ? "bg-yellow-500" : "bg-blue-500"
                  }`}
                />
                {isIndividual ? "Cá nhân" : "Doanh nghiệp"}
              </span>
            </div>

            {!isIndividual && representativeName && (
              <p className="text-sm text-gray-500 mt-1">{representativeName}</p>
            )}
          </div>
        );
      },
    },
    {
      key: "contact",
      label: "Thông tin liên hệ",
      render: (item) => (
        <div>
          <p>{item.userPhone}</p>
          <p className="text-sm text-gray-500">{item.userEmail}</p>
        </div>
      ),
    },
    {
      key: "transferImageUrl",
      label: <span className="block w-[140px] text-center">Ảnh chuyển khoản</span>,
      render: (item) => {
        const transferImageUrl = getTransferImageUrl(
          getPartnerTransferImageRawUrl(item),
        );

        if (!transferImageUrl) {
          return (
            <div className="w-[140px] flex justify-center">
              <span className="text-gray-400 italic">Không có</span>
            </div>
          );
        }

        return (
          <div className="w-[140px] flex justify-center">
            <button
              type="button"
              className="w-16 h-16 rounded-lg overflow-hidden border border-gray-200 bg-gray-50 flex items-center justify-center"
              title="Xem ảnh chuyển khoản"
              onClick={(e) => {
                e.stopPropagation();
                openTransferImageModal(item);
              }}
            >
              <img
                src={transferImageUrl}
                alt="Ảnh chuyển khoản"
                className="w-full h-full object-contain"
                loading="lazy"
              />
            </button>
          </div>
        );
      },
    },
    {
      key: "approvedAt",
      label: "Ngày duyệt",
      render: (item) =>
        renderTextOrUnknown(item?.approvedAt && formatDate(item.approvedAt)),
    },
    {
      key: "approvedBy",
      label: "Người duyệt",
      render: (item) => renderTextOrUnknown(item?.approvedByUser?.userFullName),
    },
    {
      key: "approvalStatus",
      label: "Trạng thái",
      render: (item) => {
        if (item.approvalStatus === "Từ chối") {
          return (
            <div className="min-w-[120px]">
              <button
                className="py-1 px-1 relative flex rounded-full text-sm bg-yellow-100 text-yellow-800"
                style={{ paddingRight: 34 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setFeedbackNote(item.note || "");
                  setFeedbackOpen(true);
                }}
                title="Xem phản hồi"
              >
                Đã phản hồi
                <InformationCircleIcon className="w-5 h-5 text-yellow-600 absolute right-1.5 top-1/2 -translate-y-1/2" />
              </button>
            </div>
          );
        }
        if (item.approvalStatus === "Đang chờ")
          return (
            <span className="py-1 px-2 rounded-full text-sm bg-gray-200 text-gray-700">
              Đang chờ
            </span>
          );
        if (item.approvalStatus === "Gửi duyệt")
          return (
            <span className="py-1 px-2 rounded-full text-sm bg-blue-100 text-blue-800">
              Đang gửi duyệt
            </span>
          );
        if (item.approvalStatus === "Đã duyệt")
          return (
            <span className="py-1 px-2 rounded-full text-sm bg-green-100 text-green-800">
              Đã duyệt
            </span>
          );
        return (
          <span className="py-1 px-2 rounded-full text-sm bg-gray-100 text-gray-600">
            {item.approvalStatus || "Không rõ"}
          </span>
        );
      },
    },
  ];

  if (
    hasPermission("COMPANY_UPDATE") ||
    hasPermission("COMPANY_SHOW") ||
    hasPermission("COMPANY_SHOW_LIST") ||
    hasPermission("COMPANY_DESTROY") ||
    hasPermission("APPROVE_COMPANY") ||
    hasPermission("CHANGE_POINT_USER")
  ) {
    headers.push({ key: "setting", label: "Thao tác" });
  }

  const tableSettings = {
    renderSettings: (item) => (
      <div className="flex space-x-2">
        <button
          onClick={() => handleOpenModal(item)}
          title="Chỉnh sửa"
          className="btn btn-outline btn-info btn-sm px-3 my-1 rounded"
        >
          <PencilIcon className="w-5 h-5" />
        </button>
        {hasPermission("COMPANY_UPDATE") &&
          !hasPermission("APPROVE_COMPANY") &&
          (item.approvalStatus !== "Gửi duyệt" ? (
            <button
              disabled={item.approvalStatus === "Đã duyệt"}
              onClick={() => handleSendForApproval(item.id)}
              title="Gửi duyệt hội viên"
              className="btn btn-outline btn-info btn-sm px-3 my-1 rounded"
            >
              <PaperAirplaneIcon className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={() => handleCancelSendForApproval(item.id)}
              title="Bỏ gửi duyệt"
              className="btn btn-outline btn-warning btn-sm px-3 my-1 rounded"
            >
              <XCircleIcon className="w-5 h-5" />
            </button>
          ))}

        {hasPermission("APPROVE_COMPANY") && (
          <button
            onClick={() => {
              setIsOpenApprove(true);
              setApprovingId(item.id);
            }}
            disabled={item.approvalStatus === "Đã duyệt"}
            title="Duyệt"
            className="btn btn-outline btn-accent btn-sm px-3 my-1 rounded"
          >
            {approvingId === item.id ? (
              <ArrowPathIcon className="w-5 h-5 animate-spin" />
            ) : (
              <ClipboardDocumentCheckIcon className="w-5 h-5" />
            )}
          </button>
        )}
        {/* {hasPermission("CHANGE_POINT_USER") && (
          <button
            onClick={() => {
              setIsOpenCriteria(true);
              setApprovingId(item.id);
            }}
            title="Đánh giá tiêu chí"
            className="btn btn-outline btn-warning btn-sm px-3 my-1 rounded"
          >
            <TrophyIcon className="w-5 h-5" />
          </button>
        )} */}
        {hasPermission("COMPANY_DESTROY") && (
          <button
            onClick={() => handleOpenDeleteModal(item.id)}
            title="Xóa"
            className="btn btn-outline btn-error btn-sm px-3 my-1 rounded"
          >
            <TrashIcon className="w-5 h-5" />
          </button>
        )}
      </div>
    ),
  };

  const q = (searchValue ?? "").trim().toLowerCase();

  const filterData = !q
    ? partners
    : partners?.filter((item) => {
        const rep = item?.users?.find(
          (u) => u?.CompaniesHasUsers?.isUserRepresentOfCompany,
        );
        const repName = rep?.userFullName;

        const fields = [
          item.companyName,
          item.userFullName, // nếu vẫn còn dùng ở mức top-level
          item.userPhone,
          item.userEmail,
          item.membershipFees,
          item.approvalStatus,
          repName, // <-- thêm tên đại diện vào tiêu chí lọc
        ];

        return fields.some((f) =>
          String(f ?? "")
            .toLowerCase()
            .includes(q),
        );
      });

  // ------------------ UI ------------------
  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Quản lý hội viên</h1>
      </div>

      {/* --- Bộ lọc --- */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-4 w-full">
        {/* --- BÊN TRÁI: thêm + import + lọc ngày --- */}
        <div className="flex flex-col sm:flex-row sm:items-center flex-wrap gap-3 w-full md:w-auto">
          {/* Nút thêm hội viên */}
          {hasPermission("COMPANY_CREATE") && (
            <button
              onClick={() => handleOpenModal()}
              className="btn btn-success px-4 py-2 rounded flex items-center justify-center gap-2 w-full sm:w-auto"
            >
              <PlusIcon className="w-5 h-5" />
              <span>Thêm hội viên</span>
            </button>
          )}

          {/* Import Excel */}
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <input
              type="file"
              accept=".xlsx,.xls"
              id="excelInput"
              style={{ display: "none" }}
              onChange={handleImportExcel}
              disabled={importing}
            />
            <button
              className="btn btn-outline btn-info px-3 py-2 rounded flex items-center justify-center gap-2 w-full sm:w-auto"
              onClick={() => document.getElementById("excelInput").click()}
              disabled={importing}
            >
              {importing ? "Đang nhập..." : "Nhập từ Excel"}
            </button>
          </div>

          {/* Từ ngày */}
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <label className="hidden sm:inline-block text-sm whitespace-nowrap">
              Từ ngày:
            </label>
            <div className="w-full sm:w-48">
              <DatePicker
                name="startDate"
                title="Từ ngày"
                value={startDate}
                onChange={(_, val) => setStartDate(val)}
              />
            </div>
          </div>

          {/* Đến ngày */}
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <label className="hidden sm:inline-block text-sm whitespace-nowrap">
              Đến ngày:
            </label>
            <div className="w-full sm:w-48">
              <DatePicker
                name="endDate"
                title="Đến ngày"
                value={endDate}
                onChange={(_, val) => setEndDate(val)}
              />
            </div>
          </div>

          {/* Nút lọc & Xóa lọc */}
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            <button
              className="btn btn-outline btn-info px-3 py-2 flex-1 sm:flex-none w-full sm:w-auto"
              onClick={getPartners}
            >
              Lọc
            </button>

            {(startDate || endDate) && (
              <button
                className="btn btn-outline btn-warning px-3 py-2 flex-1 sm:flex-none w-full sm:w-auto"
                onClick={resetFilters}
              >
                Xóa lọc
              </button>
            )}
          </div>
        </div>

        {/* --- BÊN PHẢI: ô tìm kiếm --- */}
        <div className="flex items-center gap-2 w-full md:w-auto md:justify-end">
          <label
            htmlFor="userSearchFilter"
            className="text-sm font-medium whitespace-nowrap"
          >
            Tìm kiếm:
          </label>
          <input
            type="search"
            id="userSearchFilter"
            className="w-full sm:w-64 px-3 py-2 border rounded-lg focus:outline-none"
            placeholder="Nhập từ khóa..."
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
              setPage(1);
            }}
          />
        </div>
      </div>

      <div className="rounded-lg shadow">
        <TableComponent
          headers={headers}
          data={filterData}
          settings={tableSettings}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </div>

      {/* --- Modals --- */}
      {isModalOpen && (
        <PartnerModal
          companyId={currentUser?.id || null}
          onClose={handleCloseModal}
          onSubmit={handleSubmitModal}
          representList={representList}
        />
      )}
      <ConfirmDeleteModal
        isOpen={isDeleteOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleDelete}
      />
      <PartnerApproveModal
        isOpen={isOpenApprove}
        onClose={handleCloseApprove}
        id={approvingId}
      />
      <PartnerCriteriaModal
        isOpen={isOpenCriteria}
        onClose={handleCloseCriteria}
        id={approvingId}
      />
      <FeedbackModal
        open={feedbackOpen}
        note={feedbackNote}
        onClose={() => setFeedbackOpen(false)}
      />

      {transferImageOpen && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <div>
                <h3 className="text-lg font-semibold">Ảnh chuyển khoản hội phí khi đăng ký</h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  {transferImageTitle}
                </p>
              </div>

              <button
                type="button"
                className="btn btn-sm btn-ghost"
                onClick={closeTransferImageModal}
              >
                ✕
              </button>
            </div>

            <div className="p-4 overflow-auto flex justify-center bg-gray-50">
              <img
                src={transferImageUrl}
                alt="Ảnh chuyển khoản"
                className="max-w-full max-h-[70vh] object-contain rounded-md shadow-sm bg-white"
              />
            </div>

            <div className="px-5 py-4 border-t flex justify-end">
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={closeTransferImageModal}
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ------------------ FEEDBACK MODAL ------------------
function FeedbackModal({ open, note, onClose }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
      <div className="bg-white rounded-xl shadow-xl p-8 w-full max-w-2xl relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center border border-gray-200 rounded-lg bg-gray-100 hover:bg-gray-200 transition"
        >
          <XCircleIcon className="w-8 h-8 text-gray-400" />
        </button>
        <div className="text-2xl font-bold mb-4">Phản hồi gửi duyệt</div>
        <div className="text-gray-700 whitespace-pre-line text-base min-h-[80px]">
          {note?.trim() ? (
            note
          ) : (
            <span className="text-gray-400">Không có nội dung phản hồi</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default Partners;
